import styled from '@emotion/styled';
import { CopyCheck, Pencil, Trash2 } from 'lucide-react';
import { Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useLingui } from '@lingui/react/macro';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeature } from 'shared/hooks/use-feature';
import { history } from 'store/store';
import { theme } from 'styles/theme/theme';
import EmptyButton from 'ud-ui/components/empty-button';
import UDTippy from 'ud-ui/components/tippy';
import { selectTemplates } from 'modules/template-editor/store/templates/selectors';
import { setActiveTemplate } from 'modules/template-editor/store/templates/templates';
import useDeleteModal from 'modules/template-editor/ui/hooks/modals/template/delete';
import useDuplicateModal from 'modules/template-editor/ui/hooks/modals/template/dublicate';
import useEditModal from 'modules/template-editor/ui/hooks/modals/template/edit';

interface IProps {
  template: Template;
  index: number;
}

function TemplateItem(props: IProps) {
  const { t } = useLingui();
  const dispatch = useDispatch();
  const { template, index } = props;

  const { isEnabled: hasObsIntegration } = useFeature('OBS_INTEGRATION');

  const templates = useSelector(selectTemplates);
  const { modal: deleteModal, open: openDeleteModal } = useDeleteModal();
  const { modal: editModal, open: openEditModal } = useEditModal();
  const { modal: duplicateModal, open: openDuplicateModal } = useDuplicateModal();

  const onClick = useCallback(() => {
    dispatch(setActiveTemplate({ id: template.id }));
    history.push(Pages.TEMPLATE.builders.boards(template.id));
  }, [dispatch, template.id]);

  const onRename = useCallback(() => {
    dispatch(setActiveTemplate({ id: template.id }));
    openEditModal();
  }, [dispatch, openEditModal, template.id]);

  const onDelete = useCallback(() => {
    dispatch(setActiveTemplate({ id: template.id }));
    openDeleteModal();
  }, [dispatch, openDeleteModal, template.id]);

  const onDuplicate = useCallback(() => {
    dispatch(setActiveTemplate({ id: template.id }));
    openDuplicateModal();
  }, [dispatch, openDuplicateModal, template.id]);

  const isDefault = useMemo(() => {
    return template.access === 'default';
  }, [template.access]);

  const isCanDelete = templates.length > 1 && !isDefault && hasObsIntegration;
  const isCanRename = !isDefault && hasObsIntegration;
  const isFeatureLocked = !hasObsIntegration;

  return (
    <CategoryItem
      key={template.name}
      style={{ backgroundColor: index % 2 === 0 ? theme.colors.SurfaceWhite : theme.colors.BorderGray50 }}
      className={'flex justify-between px-6 py-5'}
    >
      <EmptyButton style={{ fontSize: 20 }} onClick={onClick}>
        {template.name}
      </EmptyButton>
      <div className={'flex'} style={{ gap: 24 }}>
        <UDTippy content={isFeatureLocked ? t`Не доступно в вашей подписке` : t`Дублировать`} placement={'top'}>
          <span className="inline-flex">
            <EmptyButton icon={<CopyCheck size={20} />} disabled={isFeatureLocked} onClick={onDuplicate} />
          </span>
        </UDTippy>
        <UDTippy content={isFeatureLocked ? t`Не доступно в вашей подписке` : t`Редактировать`} placement={'top'}>
          <span className="inline-flex">
            <EmptyButton icon={<Pencil size={20} />} disabled={!isCanRename} onClick={onRename} />
          </span>
        </UDTippy>
        <UDTippy content={isFeatureLocked ? t`Не доступно в вашей подписке` : t`Удалить`} placement={'top'}>
          <span className="inline-flex">
            <EmptyButton color={theme.colors.error} icon={<Trash2 size={20} />} disabled={!isCanDelete} onClick={onDelete} />
          </span>
        </UDTippy>
      </div>
      {deleteModal}
      {editModal}
      {duplicateModal}
    </CategoryItem>
  );
}

export default TemplateItem;

const CategoryItem = styled.div`
  font-family:
    SF Pro Text,
    sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
`;
